import { mapActions, mapMutations, mapState, mapGetters } from "vuex";
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, LinearScale, CategoryScale } from 'chart.js';
import { EVENTS, isObjectEmpty } from "@/utils";
import { currencies_symbols } from "@/utils/currencies";
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import AppIcon from "./AppIcon.vue";
import AppInput from "@/components/AppInput";
import PercentageValue from "@/components/tableElements/PercentageValue";
import ValidateMixin from "@/mixins/ValidateMixin.vue";
import PpcBidSettingInput from "@/components/PpcBidSettingInput";
import PpcBidSimulator from "@/components/PpcBidSimulator.vue";
import AppCheckbox from "@/components/AppCheckbox.vue";
import LinkIcon from "@/components/icons/LinkIcon.vue";
import ClockRewindIcon from "@/components/icons/ClockRewindIcon.vue";
import AlertOctagon from "@/components/icons/AlertOctagon.vue";
import AppPopover from "@/components/AppPopover.vue";
import PpcBidSettingsHistoryPopover from "@/components/PpcBidSettingsHistoryPopover.vue";
import AppSwitch from "@/components/AppSwitch";
import NavigationTabs from "@/components/NavigationTabs";
import WrapWithPopover from "@/components/shared/WrapWithPopover.vue";
import Icons from "@/components/icons";
import ArrowUpRight from "./icons/ArrowUpRight.vue";
import AppModal from "@/components/AppModal";
ChartJS.register(Title, Tooltip, Legend, BarElement, LinearScale, CategoryScale);
ChartJS.register({
  id: 'legendSideTitle',
  afterDraw(chart) {
    const text = chart.options?.legendSideTitle;
    if (!text || !chart.legend) return;
    const {
      ctx
    } = chart;
    const {
      top,
      _contentHeight
    } = chart.legend;
    const contentHeight = _contentHeight ?? chart.legend.height;
    ctx.save();
    ctx.fillStyle = '#54595E';
    ctx.font = '500 15px Figtree, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, top + contentHeight / 2);
    ctx.restore();
  }
});
ChartJS.register({
  id: 'legendBottomMargin',
  beforeInit(chart) {
    if (!chart.legend) return;
    const originalFit = chart.legend.fit;
    chart.legend.fit = function fit() {
      originalFit.bind(chart.legend)();
      chart.legend._contentHeight = this.height;
      const margin = chart.options?.legendBottomMargin;
      if (margin) this.height += margin;
    };
  }
});
ChartJS.register({
  id: 'amcAnnotation',
  afterDraw(chart) {
    const value = chart.options?.amcGlobalAvgCvr;
    if (value == null || !chart.scales.y) return;
    const {
      ctx,
      chartArea: {
        left,
        right,
        top,
        bottom
      },
      scales: {
        y
      }
    } = chart;
    const yPos = y.getPixelForValue(value);
    if (yPos < top || yPos > bottom) return;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#BFBFBF';
    ctx.lineWidth = 1;
    ctx.moveTo(left, yPos);
    ctx.lineTo(right, yPos);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#ADADAD';
    ctx.font = '12px Figtree, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`Avg. ${value.toFixed(1)}%`, right, yPos - 5);
    ctx.restore();
  }
});
export default {
  name: "PpcBidSettings",
  mixins: [ValidateMixin],
  props: {
    currency: {
      type: String
    },
    selected_seller: {
      type: Object
    },
    selected_marketplace: {
      type: Object
    }
  },
  components: {
    ...Icons,
    Bar,
    AppIcon,
    PercentageValue,
    PpcBidSettingInput,
    PpcBidSimulator,
    AppCheckbox,
    LinkIcon,
    AppInput,
    ClockRewindIcon,
    AppPopover,
    PpcBidSettingsHistoryPopover,
    AlertOctagon,
    AppSwitch,
    NavigationTabs,
    WrapWithPopover,
    AppModal
  },
  data() {
    return {
      displayedValues: {},
      isEdited: [],
      isChangingBidValue: "",
      simulator: {
        non_branded_keyword_targeting: {
          product_margin: 30,
          cvr: 10
        },
        competitor_product_targeting: {
          product_margin: 30,
          cvr: 10
        },
        branded_keyword_product_targeting: {
          product_margin: 30,
          cvr: 10
        },
        discovery_targeting: {
          product_margin: 30,
          cvr: 10
        }
      },
      isValid: {
        non_branded_keyword_targeting: true,
        competitor_product_targeting: true,
        branded_keyword_product_targeting: true,
        discovery_targeting: true
      },
      biddingTypes: [{
        value: "margin",
        label: "First purchase only"
      }, {
        value: "ltv",
        label: "First + repeat purchases"
      }],
      scale_proportionally: true,
      setting_note: "",
      historical_settings: [],
      history_popover_shown: false,
      in_box_history_popover_shown: false,
      chartData: [],
      hourlyAmcData: [],
      placementAmcData: [],
      seasonalAmcData: [],
      strategicLayersTabs: [{
        text: "CVR",
        value: "cvr",
        activeBg: "#E0ECFF",
        activeColor: "#1755AB"
      }, {
        text: "Bid Modifier",
        value: "bid_modifier",
        activeBg: "#E0ECFF",
        activeColor: "#1755AB"
      }],
      currentStrategicLayersTab: 0,
      amcPlacementState: false,
      amcSeasonalityState: false,
      amcHourlyState: false,
      activeFormulaTab: {
        'growth ambition': true,
        'customer value': false,
        'timing and placement': false
      },
      currentActiveFormulaTab: 'growth ambition',
      bidSimulationModal: false
    };
  },
  computed: {
    bidSimulatorExists() {
      return !isObjectEmpty(this.bid_simulator);
    },
    ppcBidSimulatorCampaignGroupName() {
      return this.ppcBidSimulatorCampaignGroupNameGet();
    },
    ppcBidSimulatorCampaignGroupCount() {
      return this.ppcBidSimulatorCampaignGroupCountGet();
    },
    ppcBidSimulatorCampaignGroupImage() {
      return this.ppcBidSimulatorCampaignGroupImageGet();
    },
    ...mapState({
      bid_settings: state => state.ppc_settings.bid_settings,
      bid_simulator: state => state.ppc_settings.bid_simulator,
      biddingType: state => state.ppc_settings.biddingType
    }),
    isSellerCentralAccount() {
      const sellerAccounts = this.accountByProviderGet("Amazon Seller Central");
      return sellerAccounts.some(account => account.account_id?.toString() === this.selected_seller.account_id?.toString());
    },
    strategicLayersTab: {
      async set(value) {
        this.currentStrategicLayersTab = value;
      },
      get() {
        return this.currentStrategicLayersTab;
      }
    },
    ...mapGetters(["ppcBidSimulatorCampaignGroupNameGet", "ppcBidSimulatorCampaignGroupImageGet", "ppcBidSimulatorCampaignGroupCountGet", "accountByProviderGet"]),
    hourlyAmcChartOptions() {
      return this.buildAmcChartOptions(this.hourlyAmcData);
    },
    placementAmcChartOptions() {
      return this.buildAmcChartOptions(this.placementAmcData);
    },
    seasonalAmcChartOptions() {
      return this.buildAmcChartOptions(this.seasonalAmcData);
    },
    hourlyAmcChartData() {
      const isCvr = this.strategicLayersTabs[this.strategicLayersTab]?.value === 'cvr';
      return {
        labels: this.hourlyAmcData.map(item => `${item.hour_of_day_local_time}h`),
        datasets: [{
          data: this.hourlyAmcData.map(item => (parseFloat(isCvr ? item.hour_cvr : item.cvr_diff) * 100).toFixed(1)),
          backgroundColor: this.hourlyAmcData.map(item => parseFloat(isCvr ? item.hour_cvr : item.cvr_diff) >= 0 ? '#C4F2F2' : '#7ED7D7'),
          borderRadius: 6
        }]
      };
    },
    placementAmcChartData() {
      const isCvr = this.strategicLayersTabs[this.strategicLayersTab]?.value === 'cvr';
      return {
        labels: this.placementAmcData.map(item => item.placement),
        datasets: [{
          data: this.placementAmcData.map(item => (parseFloat(isCvr ? item.placement_cvr : item.cvr_diff) * 100).toFixed(1)),
          backgroundColor: this.placementAmcData.map(item => parseFloat(isCvr ? item.placement_cvr : item.cvr_diff) >= 0 ? '#C4F2F2' : '#7ED7D7'),
          borderRadius: 6
        }]
      };
    },
    seasonalAmcChartData() {
      const isCvr = this.strategicLayersTabs[this.strategicLayersTab]?.value === 'cvr';
      return {
        labels: this.seasonalAmcData.map(item => item.month_label),
        datasets: [{
          data: this.seasonalAmcData.map(item => (parseFloat(isCvr ? item.month_cvr : item.cvr_diff) * 100).toFixed(1)),
          backgroundColor: this.seasonalAmcData.map(item => parseFloat(isCvr ? item.month_cvr : item.cvr_diff) >= 0 ? '#C4F2F2' : '#7ED7D7'),
          borderRadius: 6
        }]
      };
    },
    ltvChartData() {
      if (!this.chartData.length) return {
        labels: Array(15).fill(''),
        datasets: [{
          data: Array(15).fill(-1),
          backgroundColor: '#F4F4F4',
          stack: 'margins',
          barThickness: 32
        }]
      };
      const truncate = name => name.length > 10 ? name.slice(0, 10) + '…' : name;
      return {
        labels: this.chartData.map(item => truncate(item.product_name)),
        datasets: [{
          label: 'First purchase',
          data: this.chartData.map(item => parseFloat(item.first_purchase_margin)),
          backgroundColor: '#F89053',
          stack: 'margins',
          barThickness: 32
        }, {
          label: 'Repurchase (same product)',
          data: this.chartData.map(item => parseFloat(item.repeat_entry_asin_margin)),
          backgroundColor: '#FEC9AA',
          stack: 'margins',
          barThickness: 32
        }, {
          label: 'Cross-sell (other products)',
          data: this.chartData.map(item => parseFloat(item.repeat_other_asin_margin)),
          backgroundColor: '#FFE9DB',
          stack: 'margins',
          barThickness: 32
        }]
      };
    },
    ltvChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legendSideTitle: 'Customer value per ASIN (top 15)',
        legendBottomMargin: 24,
        plugins: {
          legend: {
            display: !!this.chartData.length,
            position: 'top',
            align: 'end',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxHeight: 8
            }
          },
          tooltip: {
            callbacks: {
              title: items => this.chartData[items[0]?.dataIndex]?.product_name ?? items[0]?.label,
              label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} ${currencies_symbols[this.currency] || this.currency}`
            }
          },
          datalabels: {
            display: false
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: 'Figtree, sans-serif',
                size: 11
              }
            }
          },
          y: {
            stacked: true,
            grid: {
              display: true,
              drawBorder: false
            },
            ticks: {
              display: !!this.chartData.length,
              font: {
                family: 'Figtree, sans-serif',
                size: 11
              },
              callback: value => `${value} ${currencies_symbols[this.currency] || this.currency}`
            }
          }
        }
      };
    }
  },
  async mounted() {
    this.updateBidSettingsToDisplay();
    this.getHistoricalData();
    this.ppcBidSettingsBiddingTypeSet(this.bid_settings?.bidding_type || 'margin');
    this.amcHourlyState = this.bid_settings?.bid_modifier_hourly || false;
    this.amcPlacementState = this.bid_settings?.bid_modifier_placement || false;
    // this.amcSeasonalityState = this.bid_settings?.bid_modifier_seasonal || false;
    if (this.isSellerCentralAccount) {
      const result = await this.ppcBidSettingsLtvGet({
        seller_id: [this.selected_seller?.account_id?.toString()],
        sales_channel: [this.selected_marketplace?.sales_channel],
        currency: this.currency
      });
      this.chartData = result?.data ?? [];
      const amcPayload = {
        seller_id: [this.selected_seller?.account_id?.toString()],
        sales_channel: [this.selected_marketplace?.sales_channel]
      };
      const hourlyResult = await this.ppcBidSettingsHourlyAmcGet(amcPayload);
      this.hourlyAmcData = hourlyResult?.data ?? [];
      const placementResult = await this.ppcBidSettingsPlacementAmcGet(amcPayload);
      this.placementAmcData = placementResult?.data ?? [];
      const seasonalResult = await this.ppcBidSettingsSeasonalAmcGet(amcPayload);
      this.seasonalAmcData = seasonalResult?.data ?? [];
    }
  },
  watch: {
    bid_settings(new_bid_settings) {
      this.updateBidSettingsToDisplay();
      this.getHistoricalData();
      this.ppcBidSettingsBiddingTypeSet(new_bid_settings?.bidding_type || 'margin');
      this.amcHourlyState = new_bid_settings?.bid_modifier_hourly || false;
      this.amcPlacementState = new_bid_settings?.bid_modifier_placement || false;
      // this.amcSeasonalityState = new_bid_settings?.bid_modifier_seasonal || false;
    }
  },
  methods: {
    buildAmcChartOptions(data) {
      const globalAvgCvr = data.length ? parseFloat(data[0].global_avg_cvr) * 100 : null;
      return {
        responsive: true,
        maintainAspectRatio: false,
        amcGlobalAvgCvr: globalAvgCvr,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: ctx => `${ctx.parsed.y.toFixed(1)}%`
            }
          },
          datalabels: {
            display: false
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: 'Figtree, sans-serif',
                size: 11
              }
            }
          },
          y: {
            grid: {
              display: true,
              drawBorder: false
            },
            ticks: {
              font: {
                family: 'Figtree, sans-serif',
                size: 11
              },
              callback: value => `${value}%`
            }
          }
        }
      };
    },
    ...mapActions(["ppcBidSettingSave", "ppcHistoricalBidSettingGet", "ppcBidSettingsLtvGet", "ppcBidSettingsHourlyAmcGet", "ppcBidSettingsPlacementAmcGet", "ppcBidSettingsSeasonalAmcGet"]),
    ...mapMutations(["ppcBidSettingsBiddingTypeSet"]),
    buildBidSettingsPayload(overrides = {}) {
      return {
        seller_id: this.bid_settings.seller_id,
        profile_id: this.bid_settings.profile_id,
        marketplace_id: this.bid_settings.marketplace_id,
        bid_settings_id: `${this.bid_settings.seller_id}_${this.bid_settings.profile_id}_${this.bid_settings.marketplace_id}`,
        non_branded_keyword_targeting: this.displayedValues.non_branded_keyword_targeting / 100,
        branded_keyword_product_targeting: this.displayedValues.branded_keyword_product_targeting / 100,
        competitor_product_targeting: this.displayedValues.competitor_product_targeting / 100,
        discovery_targeting: this.displayedValues.discovery_targeting / 100,
        bidding_type: this.biddingType,
        bid_modifier_placement: this.amcPlacementState,
        bid_modifier_seasonal: this.amcSeasonalityState,
        bid_modifier_hourly: this.amcHourlyState,
        ...overrides
      };
    },
    async onBiddingTypeChange(newType) {
      this.ppcBidSettingsBiddingTypeSet(newType);
      const payload = this.buildBidSettingsPayload({
        bidding_type: newType,
        notes: `Customer margin type changed to ${this.biddingTypes?.filter(item => item.value === newType)?.[0]?.label}`
      });
      await this.ppcBidSettingSave({
        data: [payload]
      });
      window.$bus.trigger(EVENTS.MIXPANEL_TRACK, {
        event_name: 'advertisingBidSettingsUpdated',
        properties: {}
      });
      this.getHistoricalData();
    },
    async onAmcToggleChange(type, newValue) {
      const stateKey = {
        placement: 'amcPlacementState',
        seasonality: 'amcSeasonalityState',
        hourly: 'amcHourlyState'
      }[type];
      const payloadKey = {
        placement: 'bid_modifier_placement',
        seasonality: 'bid_modifier_seasonal',
        hourly: 'bid_modifier_hourly'
      }[type];
      this[stateKey] = newValue;
      const payload = this.buildBidSettingsPayload({
        [payloadKey]: newValue,
        notes: `AMC ${type} modifier ${newValue ? 'enabled' : 'disabled'}`
      });
      await this.ppcBidSettingSave({
        data: [payload]
      });
      window.$bus.trigger(EVENTS.MIXPANEL_TRACK, {
        event_name: 'advertisingBidSettingsUpdated',
        properties: {}
      });
      this.getHistoricalData();
    },
    async getHistoricalData() {
      const historical_settings_result = await this.ppcHistoricalBidSettingGet({
        seller_list: [this.bid_settings.seller_id],
        profile_list: [this.bid_settings.profile_id]
      });
      this.historical_settings = historical_settings_result;
    },
    updateBidSettingsToDisplay() {
      this.displayedValues = {
        non_branded_keyword_targeting: Math.round(this.bid_settings.non_branded_keyword_targeting * 100),
        branded_keyword_product_targeting: Math.round(this.bid_settings.branded_keyword_product_targeting * 100),
        competitor_product_targeting: Math.round(this.bid_settings.competitor_product_targeting * 100),
        discovery_targeting: Math.round(this.bid_settings.discovery_targeting * 100)
      };
    },
    onChangeValue(refName) {
      if (!this.isEdited.some(item => item === refName)) {
        this.isEdited.push(refName);
      }
    },
    isChangedValue(refName) {
      return this.isEdited.some(item => item === refName);
    },
    onReverseClick(refName) {
      if (this.scale_proportionally) {
        this.reverseAllValue();
        return;
      }
      this.reverseValue(refName);
    },
    reverseValue(refName) {
      this.displayedValues[refName] = Math.round(this.bid_settings[refName] * 100);
      this.isEdited = this.isEdited.filter(item => item !== refName);
    },
    reverseAllValue() {
      this.isEdited.map(refName => {
        this.reverseValue(refName);
      });
    },
    onChangeBidValue(refName) {
      this.isChangingBidValue = refName;
      setTimeout(() => {
        this.$refs[refName].focus();
      }, 500);
    },
    async onBlur(refName) {
      this.isChangingBidValue = "";
      if (this.displayedValues[refName] * 1 !== Math.round(this.bid_settings[refName] * 100)) {
        this.onChangeValue(refName);
        if (this.scale_proportionally) {
          const percentChange = (this.displayedValues[refName] - Math.round(this.bid_settings[refName] * 100)) / Math.round(this.bid_settings[refName] * 100);
          Object.keys(this.displayedValues).forEach(item => {
            if (item !== refName) {
              this.displayedValues[item] = Math.round(this.bid_settings[item] * 100) + percentChange * Math.round(this.bid_settings[item] * 100);
              this.onChangeValue(item);
            }
          });
        }
        return;
      }
      this.reverseAllValue();
    },
    async onSubmit() {
      const isValid = Object.values(this.isValid).every(targeting => targeting);
      if (!isValid || this.setting_note.length < 10) return;
      let payload = {
        seller_id: this.bid_settings.seller_id,
        profile_id: this.bid_settings.profile_id,
        marketplace_id: this.bid_settings.marketplace_id,
        bid_settings_id: `${this.bid_settings.seller_id}_${this.bid_settings.profile_id}_${this.bid_settings.marketplace_id}`,
        non_branded_keyword_targeting: this.displayedValues.non_branded_keyword_targeting / 100,
        branded_keyword_product_targeting: this.displayedValues.branded_keyword_product_targeting / 100,
        competitor_product_targeting: this.displayedValues.competitor_product_targeting / 100,
        discovery_targeting: this.displayedValues.discovery_targeting / 100,
        bidding_type: this.biddingType,
        notes: this.setting_note,
        bid_modifier_placement: this.amcPlacementState,
        bid_modifier_seasonal: this.amcSeasonalityState,
        bid_modifier_hourly: this.amcHourlyState
      };
      await this.ppcBidSettingSave({
        data: [payload]
      });
      window.$bus.trigger(EVENTS.MIXPANEL_TRACK, {
        event_name: 'advertisingBidSettingsUpdated',
        properties: {}
      });
      this.getHistoricalData();
      this.isEdited = [];
      this.setting_note = "";
    },
    onCloseBidSettingHistory(value) {
      document.getElementById(value).click();
    },
    async changeActiveFormulaTab(newValue) {
      if (newValue === this.currentActiveFormulaTab) {
        return;
      }
      Object.keys(this.activeFormulaTab)?.forEach(key => {
        this.activeFormulaTab[key] = false;
      });
      this.activeFormulaTab[newValue] = true;
      this.currentActiveFormulaTab = newValue;
      this.$refs?.contentContainer?.click();
      await this.$nextTick();
      return;
    }
  }
};